import React from 'react'
import { X } from 'lucide-react'
import { Button } from 'react-bootstrap'

interface KeyboardShortcutsPanelProps {
  onClose: () => void
  mode?: 'labeling' | 'review' // 'labeling' for regular annotation view, 'review' for AAL Review modal
}

const KeyboardShortcutsPanel: React.FC<KeyboardShortcutsPanelProps> = ({
  onClose,
  mode = 'labeling',
}) => {
  const allShortcuts = [
    {
      category: 'Review Actions',
      mode: 'review', // Only show in AAL Review modal
      items: [
        { keys: ['A'], description: 'Accept the selected box' },
        { keys: ['R'], description: 'Reject the selected box' },
        { keys: ['I'], description: 'Mark as Needs Inspection' },
      ],
    },
    {
      category: 'Navigation',
      mode: 'both',
      items: [
        { keys: ['↑', '/', '↓'], description: 'Select previous/next box' },
        { keys: ['Shift', 'P'], description: 'Navigate to previous image' },
        { keys: ['Shift', 'N'], description: 'Navigate to next image' },
      ],
    },
    {
      category: 'Editing',
      mode: 'both',
      items: [
        { keys: ['Ctrl/Cmd', 'Z'], description: 'Undo last action' },
        { keys: ['Ctrl/Cmd', 'Shift', 'Z'], description: 'Redo last action' },
        { keys: ['Delete'], description: 'Delete selected box' },
        { keys: ['Escape'], description: 'Cancel OBB creation' },
      ],
    },
    {
      category: 'View Controls',
      mode: 'both',
      items: [
        { keys: ['Mouse Wheel'], description: 'Zoom in/out' },
        { keys: ['Space', '+', 'Drag'], description: 'Pan the image' },
        { keys: ['Ctrl/Cmd', '+', 'Click'], description: 'Force create new box' },
      ],
    },
    {
      category: 'Submit',
      mode: 'both',
      items: [{ keys: ['Enter'], description: 'Submit annotations' }],
    },
  ]

  // Filter shortcuts based on mode
  const shortcuts = allShortcuts.filter(
    (section) => section.mode === 'both' || section.mode === mode
  )

  return (
    <div
      className="d-flex flex-column"
      style={{
        backgroundColor: '#0d1117',
        borderRight: '1px solid rgba(255, 255, 255, 0.1)',
        height: '100%',
        maxHeight: '100%',
        minHeight: 0,
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <div
        className="d-flex align-items-center justify-content-between p-3"
        style={{
          backgroundColor: '#161b22',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          flexShrink: 0,
        }}
      >
        <h6 className="text-light mb-0 fw-bold">⌨️ Keyboard Shortcuts</h6>
        <Button
          variant="link"
          size="sm"
          onClick={onClose}
          style={{ padding: 0, color: '#8b949e' }}
          aria-label="Close shortcuts panel"
        >
          <X size={18} />
        </Button>
      </div>

      {/* Content */}
      <div
        className="flex-grow-1 p-3"
        style={{
          minHeight: 0,
          maxHeight: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        <div className="d-flex flex-column gap-4">
          {shortcuts.map((section, sectionIdx) => (
            <div key={sectionIdx}>
              <h6 className="text-light mb-2 fw-bold" style={{ fontSize: '0.875rem' }}>
                {section.category}
              </h6>
              <div className="d-flex flex-column gap-2">
                {section.items.map((shortcut, itemIdx) => (
                  <div
                    key={itemIdx}
                    className="d-flex flex-column gap-1 p-2"
                    style={{
                      backgroundColor: '#161b22',
                      borderRadius: '6px',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                    }}
                  >
                    <div className="d-flex flex-wrap gap-1">
                      {shortcut.keys.map((key, keyIdx) => (
                        <React.Fragment key={keyIdx}>
                          <kbd
                            style={{
                              padding: '0.15rem 0.4rem',
                              backgroundColor: '#1f2937',
                              border: '1px solid #374151',
                              borderRadius: '4px',
                              fontSize: '0.7rem',
                              fontWeight: '600',
                              color: '#e6edf3',
                              boxShadow: '0 2px 0 rgba(0,0,0,0.2)',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {key}
                          </kbd>
                          {keyIdx < shortcut.keys.length - 1 && (
                            <span style={{ color: '#6b7280', fontSize: '0.75rem' }}>+</span>
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                    <span style={{ fontSize: '0.75rem', color: '#8b949e' }}>
                      {shortcut.description}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Tip */}
        <div
          className="mt-4 p-2 d-flex align-items-start gap-2"
          style={{
            backgroundColor: '#1f2937',
            borderRadius: '6px',
            border: '1px solid rgba(59, 130, 246, 0.3)',
          }}
        >
          <span style={{ fontSize: '1rem' }}>💡</span>
          <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
            <strong className="text-light">Tip:</strong> Click on a snippet or box to select it
            first.
          </div>
        </div>
      </div>
    </div>
  )
}

export default KeyboardShortcutsPanel
